import { useCallback, useEffect, useRef } from 'react';

type ScrollIntoViewOptionsArg = Exclude<Parameters<HTMLElement['scrollIntoView']>[0], boolean | undefined>;

const DEFAULT_SCROLL_OPTIONS: ScrollIntoViewOptionsArg = { behavior: 'smooth', block: 'center' };

/**
 * Scrolls an element into view when a condition becomes true.
 *
 * Returns a ref callback that composes with another ref callback, such as
 * the setNodeRef provided by dnd-kit's useSortable.
 *
 * @param {boolean} isActive - Scrolls when this flips to true
 * @param {Function} externalRef - Optional ref callback to forward the node to
 * @param {ScrollIntoViewOptions} options - Scroll options
 * @returns {Function} Ref callback to attach to the element
 */
const useAutoScrollToBottom = <T extends HTMLElement>(
    isActive: boolean,
    externalRef?: (node: T | null) => void,
    options: ScrollIntoViewOptionsArg = DEFAULT_SCROLL_OPTIONS
): ((node: T | null) => void) => {
    const nodeRef = useRef<T | null>(null);
    const optionsRef = useRef(options);

    optionsRef.current = options;

    const setRefs = useCallback(
        (node: T | null) => {
            nodeRef.current = node;
            externalRef?.(node);
        },
        [externalRef]
    );

    useEffect(() => {
        if (!isActive) return;

        nodeRef.current?.scrollIntoView?.(optionsRef.current);
    }, [isActive]);

    return setRefs;
};

export default useAutoScrollToBottom;
